// ReviewNotes — banner shown above a post (or on the compose form) when an
// editor has returned it with feedback. Author sees the editor's name and
// note; the post's status stays 'pending' until the author resubmits.

import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../lib/api';

interface Note {
    id:           number;
    post_id:      string;
    community_id: string;
    editor_id:    string;
    editor_name:  string;
    note:         string;
    created_at:   string;
    resolved_at?: string | null;
}

interface Props {
    /** When provided, shows notes for this specific post. Otherwise shows
     *  the caller's unresolved notes across all their posts (dashboard use). */
    postID?: string;
}

export function ReviewNotes({ postID }: Props) {
    const api = useApi();
    const [notes, setNotes] = useState<Note[]>([]);
    const [err, setErr] = useState('');

    const load = useCallback(async () => {
        try {
            const url = postID
                ? `/api/posts/${postID}/review-notes`
                : '/api/me/review-notes';
            const r = await api.get<{ notes: Note[] }>(url);
            // Only show unresolved on the compose surface (postID unset) —
            // resolved notes are historical noise. On a specific post,
            // show everything for full context.
            const filtered = postID
                ? (r.notes ?? [])
                : (r.notes ?? []).filter(n => !n.resolved_at);
            setNotes(filtered);
        } catch (e) { setErr((e as Error).message); }
    }, [api, postID]);
    useEffect(() => { load(); }, [load]);

    if (err)         return null; // silent — banner is optional UI
    if (notes.length === 0) return null;

    return (
        <div className="review-notes-banner" style={bannerStyle}>
            <div style={headerStyle}>
                {postID
                    ? `${notes.length} editor note${notes.length === 1 ? '' : 's'} on this post`
                    : `${notes.length} post${notes.length === 1 ? '' : 's'} waiting for your response`}
            </div>
            {notes.map(n => (
                <div key={n.id} style={noteStyle}>
                    <div style={metaStyle}>
                        <strong>{n.editor_name}</strong>
                        <span> · {new Date(n.created_at).toLocaleString()}</span>
                        {n.resolved_at && <em style={{ color: '#699a3f', marginLeft: 8 }}>resolved</em>}
                    </div>
                    <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{n.note}</div>
                </div>
            ))}
        </div>
    );
}

const bannerStyle: React.CSSProperties = {
    padding: 12, margin: '10px 0',
    background: '#fff4d6', border: '1px solid #d4a017',
    fontFamily: '-apple-system, sans-serif', fontSize: 13,
};
const headerStyle: React.CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: '#7a5c00', marginBottom: 8,
};
const noteStyle: React.CSSProperties = {
    padding: '6px 0', borderTop: '1px dotted #d4a017',
};
const metaStyle: React.CSSProperties = {
    fontSize: 11, color: '#666',
};
